import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import {
  trackWorkEvent,
  WorkLaunchEventName,
  type WorkSurface,
} from '@/models/analytics/features/work';
import {
  linkedContextItemAddMenuPickType,
  WORK_HOME_COMPOSER_ENTRY,
  workHomeSuggestionAnalyticsKind,
  type AddMenuPickType,
  type AddMenuSubmenuType,
  type WorkHomeComposerRoutineActionedProps,
  type WorkHomeSuggestionAnalyticsProps,
} from '@heidi/agents/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

// AGENTS-OWNED, re-exported name-intact. The tokens, their two argument unions
// and the `library_document -> sources` pick mapper are the composer's analytics
// vocabulary and now have one definition site, in `@heidi/agents/contracts`, so
// the composer chrome that lives in that package builds the same `entry_point`
// strings the app emits. The EMITTERS below stay here: they reach the analytics
// pipeline, which a rendered surface in a package never does.
export { linkedContextItemAddMenuPickType, WORK_HOME_COMPOSER_ENTRY };
export type { AddMenuPickType, AddMenuSubmenuType };

type ComposerAnalyticsProps = {
  surface?: WorkSurface;
  work_mode?: string;
};

export function trackWorkHomeContextSelected(
  label: 'session' | 'patient',
  props: { click_source: 'activity_sessions' | 'activity_patients'; session_id?: string }
): void {
  trackButtonClicked({
    screen_name: ScreenName.Heidi2WorkHome,
    action: ButtonAction.Heidi2WorkHomeContextSelected,
    label,
    ...props,
  });
}

function resolveSurface(surface?: WorkSurface): WorkSurface {
  return surface ?? 'work_home';
}

/** Panel/menu opened on the Work home composer. */
export function trackWorkHomeComposerViewed(
  entryPoint: string,
  props: ComposerAnalyticsProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'viewed',
    surface: resolveSurface(props.surface),
    entry_point: entryPoint,
    ...(props.work_mode ? { work_mode: props.work_mode } : {}),
  });
}

/** User clicked an affordance on the Work home composer. */
export function trackWorkHomeComposerCta(
  entryPoint: string,
  props: ComposerAnalyticsProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'cta_clicked',
    surface: resolveSurface(props.surface),
    entry_point: entryPoint,
    ...(props.work_mode ? { work_mode: props.work_mode } : {}),
  });
}

/** Activity panel closed on Work home. */
export function trackWorkHomeComposerDismissed(
  entryPoint: string,
  props: ComposerAnalyticsProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'dismissed',
    surface: resolveSurface(props.surface),
    entry_point: entryPoint,
    ...(props.work_mode ? { work_mode: props.work_mode } : {}),
  });
}

/**
 * A Work home starter row was picked. The row's kind rides in `entry_point`,
 * which is how `trackSidebarNewChatClicked` already carries its click source —
 * so which starters earn their place is readable with no schema change.
 */
export function trackWorkHomeSuggestionPicked(
  suggestionId: string,
  { surface, work_mode, ...features }: WorkHomeSuggestionAnalyticsProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'cta_clicked',
    surface: resolveSurface(surface),
    entry_point: `suggestion_${workHomeSuggestionAnalyticsKind(suggestionId)}`,
    ...(work_mode ? { work_mode } : {}),
    ...features,
  });
}

/**
 * A Work home starter row rendered. Pairs 1:1 with
 * `trackWorkHomeSuggestionPicked` on `surface` and `entry_point`.
 *
 * `action` is `viewed` because that is the Iglu-legal impression verb
 * (`shown` is not on `WorkSurfaceActionedAction`). A row impression is
 * the `suggestion_*` entry_point, not the surface-level `viewed` that
 * has no suggestion prefix — dashboard queries must filter on
 * `entry_point`.
 */
export function trackWorkHomeSuggestionShown(
  suggestionId: string,
  { surface, work_mode, ...features }: WorkHomeSuggestionAnalyticsProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'viewed',
    surface: resolveSurface(surface),
    entry_point: `suggestion_${workHomeSuggestionAnalyticsKind(suggestionId)}`,
    ...(work_mode ? { work_mode } : {}),
    ...features,
  });
}

/** Routine invoked from the composer + menu (actually runs the workflow). */
export function trackWorkHomeComposerRoutineActioned(
  props: WorkHomeComposerRoutineActionedProps
): void {
  trackWorkEvent(WorkLaunchEventName.WorkWorkflowActioned, {
    action: 'run_clicked',
    object_type: 'workflow',
    surface: resolveSurface(props.surface),
    trigger_source: props.trigger_source,
    ...(props.workflow_id ? { workflow_id: props.workflow_id } : {}),
    ...(props.work_mode ? { work_mode: props.work_mode } : {}),
  });
}
